import React, { useState } from 'react';
import S from './style/Dac7IbanField';
import V, { Validity } from '../utils/validation';
import { useI18n } from '../../../../../i18n';
import ibanValidator from '../utils/ibanValidator';
import plIbanMask from '../utils/plIbanMask';
import { Dac7FormInputName } from '../types/Dac7FormInputName';

interface Dac7IbanFieldInnerProps {
  value: string;
  validity: Validity;
  label: string;
  name: Dac7FormInputName;
  required: boolean;
  disabled: boolean;
  onChange?: (value: string, validity: Validity) => void;
  onBlur?: (event: React.SyntheticEvent) => void;
}

const Dac7IbanFieldInner = ({
  value,
  validity,
  name,
  label,
  required,
  disabled,
  onChange,
  onBlur,
}: Dac7IbanFieldInnerProps) => {
  const i18n = useI18n();
  const ref = React.createRef<HTMLInputElement>();
  const [isTouched, setIsTouched] = useState(false);

  const validate = (currentValue: string): Validity => {
    const isIbanValid = ibanValidator(currentValue);
    if (required && currentValue === '') {
      const requiredMessage = `${i18n('dlv_dac7_validation_required')}`;
      return { valid: false, error: requiredMessage };
    }

    if (!isIbanValid) {
      const patternMessage = `${i18n('dlv_dac7_iban_error_is_invalid')}`;
      return { valid: false, error: patternMessage };
    }

    return { valid: true, error: null };
  };
  const showValidation = !validity.valid && isTouched;

  const handleBlur = (e: React.SyntheticEvent) => {
    if (onBlur) onBlur(e);
    const currentValue = ref.current.value;
    const currentValidity = validate(currentValue);
    const maskedIban = currentValue.length > 0 ? plIbanMask(currentValue) : '';
    onChange(maskedIban, currentValidity);
    setIsTouched(true);
  };

  const handleChange = () => {
    const currentValue = ref.current.value;
    const currentValidity = validate(currentValue);
    const maskedIban = currentValue.length > 0 ? plIbanMask(currentValue) : '';
    onChange(maskedIban, currentValidity);
    setIsTouched(true);
  };

  return (
    <S.Wrapper>
      <S.StyledTextField
        name={name}
        label={label}
        placeholder="PL __-____-____-____-____-____-____"
        status={showValidation ? 'invalid' : 'default'}
        pattern={V.IBAN_PATTERN}
        value={value || ''}
        type="text"
        required={required}
        disabled={disabled}
        error={validity.error}
        onChange={handleChange}
        onBlur={handleBlur}
        ref={ref}
      />
    </S.Wrapper>
  );
};

interface Dac7IbanFieldProps {
  value: string;
  label: string;
  name: Dac7FormInputName;
  required: boolean;
  disabled: boolean;
}

const Dac7IbanField = ({ value, ...rest }: Dac7IbanFieldProps) => {
  const maskedIban = value.length > 0 ? plIbanMask(value) : '';
  const [iban, setIban] = useState(maskedIban);
  const [validity, setValidity] = useState<Validity>({
    valid: true,
    error: '',
  });

  const ibanChanged = (currentIbanValue: string, currentValidity: Validity): void => {
    setIban(currentIbanValue);
    setValidity(currentValidity);
  };

  return <Dac7IbanFieldInner value={iban} validity={validity} onChange={ibanChanged} {...rest} />;
};

export default Dac7IbanField;
